import { useEffect } from "react";
import { FieldValues, UseFormReturn, useWatch } from "react-hook-form";
import { useDebounceCallback, useEventCallback } from "usehooks-ts";

import { AUTO_SAVE_DEBOUNCE_MS } from "@/constants/forms";

import useOnValueChanged from "./useOnValueChanged";

export const useDebouncedFormValuesChanged = <FormType extends FieldValues>(
  form: UseFormReturn<FormType>,
  onValuesChanged: (formValues: FormType) => void,
  debounceMs = AUTO_SAVE_DEBOUNCE_MS,
) => {
  const formValues = useWatch<FormType>({ control: form.control });

  const debouncedCallback = useDebounceCallback(onValuesChanged, debounceMs);

  useOnValueChanged(formValues, () => {
    debouncedCallback(form.getValues());
  });

  const cleanup = useEventCallback(() => {
    if (debouncedCallback.isPending()) {
      debouncedCallback.flush();
    }
  });

  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);
};
